import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CircleX } from 'lucide-react'
import { otpSendWait, resendEmailCode, startEmailSetup, verifyEmailSetup } from '../../api/mfa'
import { keys } from '../../api/keys'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { Icon } from '../../platform/icons/Icon'
import { Overlay } from '../../platform/overlay/Overlay'
import { useToast } from '../../platform/feedback/ToastProvider'
import { ReauthDialog } from './ReauthDialog'
import { StepUpSheet } from './StepUpSheet'
import { CANCELLED_MESSAGE, mfaMessage } from './mfaCopy'
import { EMAIL_CODE_LIFETIME_SECONDS, useEmailCodeExpiry } from './useEmailCodeExpiry'
import { useGatedMfaCall } from './useGatedMfaCall'
import { heldLabel, resendLabel, useResendCooldown } from './useResendCooldown'
import './mfaSheet.css'

type Props = { open: boolean; onClose: () => void }

/**
 * Turn on email codes — a full-screen sheet, one step per screen (M13-R28).
 * Send the code to the account's own address, enter it, verify; resend on
 * request. The address shown is the backend's masked form.
 *
 * Email codes confirm sensitive actions. The sheet says so and does not claim
 * they protect sign-in. The typed code lives in this component's state and is
 * never logged or sent to analytics (rule 7).
 */
export function EmailCodesSheet({ open, onClose }: Props) {
  const queryClient = useQueryClient()
  const close = useCallback(() => {
    onClose()
    void queryClient.invalidateQueries({ queryKey: keys.mfa.status() })
  }, [onClose, queryClient])
  return (
    <Overlay open={open} onClose={close} label="Email codes" placement="sheet">
      {open ? <Flow onClose={close} /> : null}
    </Overlay>
  )
}

function Flow({ onClose }: { onClose: () => void }) {
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { show } = useToast()
  const inputRef = useRef<HTMLInputElement>(null)
  const started = useRef(false)

  /**
   * Sends the enrolment code. A backend step-up (a user who already has a proof
   * factor) opens `StepUpSheet`, and its single-use token comes back for
   * exactly one retry — `useGatedMfaCall` owns that sequence.
   */
  const cooldown = useResendCooldown()
  const { start: startCooldown } = cooldown
  // A refused send writes nothing, so a code sent earlier still works until it expires. The backend
  // does not say whether one exists; this tab's record of its expiry is the only evidence (a reload
  // loses it, and the sheet then shows the failure screen with the wait, as for a first send).
  const expiry = useEmailCodeExpiry()
  const [held, setHeld] = useState(false)
  // The limit note on the held screen; a successful send or resend makes it stale, so it goes.
  const [limitNote, setLimitNote] = useState(false)
  const send = useGatedMfaCall({
    call: startEmailSetup,
    onLimited: (wait) => {
      startCooldown(wait)
      if (expiry.holdsLiveCode()) {
        setHeld(true)
        setLimitNote(true)
      }
    },
    onDone: ({ maskedEmail: masked, expiresInSeconds }) => {
      expiry.remember(expiresInSeconds)
      setLimitNote(false)
      setMaskedEmail(masked)
      startCooldown()
    },
  })
  const { run: sendCode } = send

  // The code is sent as the sheet opens. StrictMode runs effects twice in dev;
  // the ref keeps that to one email.
  useEffect(() => {
    if (started.current) return
    started.current = true
    void sendCode()
  }, [sendCode])

  const entering = maskedEmail !== null || held
  useEffect(() => {
    if (entering) inputRef.current?.focus()
  }, [entering])

  const failure =
    send.error ?? (send.cancelled ? CANCELLED_MESSAGE : send.waiting ? 'Confirm it’s you to send the code.' : null)

  async function verify(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      await verifyEmailSetup(code.trim())
      expiry.forget()
      show({ kind: 'success', message: 'Email codes are on.' })
      onClose()
    } catch (thrown) {
      setError(mfaMessage(thrown))
      setBusy(false)
      inputRef.current?.focus()
    }
  }

  async function resend() {
    setBusy(true)
    setError(null)
    try {
      await resendEmailCode()
      expiry.remember(EMAIL_CODE_LIFETIME_SECONDS)
      setLimitNote(false)
      startCooldown()
      show({ kind: 'success', message: 'We sent a new code.' })
    } catch (thrown) {
      const wait = otpSendWait(thrown)
      if (wait !== null) startCooldown(wait)
      setError(mfaMessage(thrown))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mfa-sheet">
      <h2 className="dialog__title">Turn on email codes</h2>
      {!entering && failure === null && <p className="dialog__body">Sending a code to your email…</p>}
      {!entering && failure !== null && (
        <>
          <p className="dialog__error" role="alert">
            <span className="dialog__error-mark">
              <Icon glyph={CircleX} />
            </span>
            {failure}
          </p>
          <div className="dialog__actions">
            <Button variant="primary" size="page" disabled={send.busy || cooldown.secondsLeft > 0} onClick={() => void send.run()}>
              {heldLabel('Try again', cooldown.secondsLeft)}
            </Button>
            <Button variant="outline" size="page" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </>
      )}
      {entering && (
        <form className="mfa-sheet" onSubmit={(e) => void verify(e)}>
          <p className="dialog__body">
            {maskedEmail !== null ? `We sent a 6-digit code to ${maskedEmail}.` : 'We sent you a 6-digit code earlier.'} Email codes
            confirm sensitive changes on your account. They don&apos;t sign you in.
          </p>
          {limitNote && (
            <p className="dialog__body" role="status">
              {send.error !== null && `${send.error} `}Enter the code you already have.
            </p>
          )}
          <Field
            label="Code from your email"
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            {...(error !== null ? { error } : {})}
          />
          <div className="dialog__actions">
            <Button type="submit" variant="primary" size="page" disabled={busy || code.trim().length !== 6}>
              {busy ? 'Checking…' : 'Verify'}
            </Button>
            <Button type="button" variant="outline" size="page" disabled={busy || cooldown.secondsLeft > 0} onClick={() => void resend()}>
              {resendLabel(cooldown.secondsLeft)}
            </Button>
            <Button type="button" variant="outline" size="page" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>
      )}
      <ReauthDialog
        {...send.reauthProps}
        mode="enrol_email_codes_reauth"
        title="Confirm it's you"
        body="For your security, confirm your identity again before turning on email codes."
        confirmLabel="Continue"
        pendingLabel="Checking…"
      />
      <StepUpSheet {...send.stepUpProps} />
    </div>
  )
}
